import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import IHappinessRating from '../../common/models/IHappinessRating';
import * as moment from 'moment';
import { DateFormat } from '../../common/utils/dateUtils';

type CalendarView = 'monthly' | 'yearly';

export interface CalendarState {
  entries: Array<IHappinessRating>;
  isBusy: boolean;
  dayComment?: string;
  dayScore?: number;
  displayedYear?: number;
  displayedDate: string;
  selectedView: CalendarView;
}

const initialState: CalendarState = {
  entries: [],
  isBusy: false,
  displayedDate: moment.utc().format(DateFormat.LEGACY_MOMENT_SHORT_ISO),
  displayedYear: moment.utc().year(),
  selectedView: 'monthly'
};

export const calendarSlice = createSlice({
  name: 'calendar',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setDayComment: (state, action: PayloadAction<string>) => {
      state.dayComment = action.payload;
    },
    setDayScore: (state, action: PayloadAction<number>) => {
      state.dayScore = action.payload;
    },
    setCalendarEntries: (
      state,
      action: PayloadAction<Array<IHappinessRating>>
    ) => {
      state.entries = action.payload;
    },
    setDisplayedDate: (state, action: PayloadAction<string>) => {
      state.displayedDate = action.payload;
    },
    setDisplayedYear: (state, action: PayloadAction<number>) => {
      state.displayedYear = action.payload;
    },
    addCalendarEntryRequest: (state) => {
      state.isBusy = true;
    },
    addCalendarEntryResponse: (state) => {
      state.isBusy = false;
    },
    deleteCalendarEntryRequest: (
      state,
      action: PayloadAction<IHappinessRating>
    ) => {
      state.isBusy = true;
      state.dayComment = action.payload.comment;
      state.dayScore = action.payload.score;
    },
    deleteCalendarEntryResponse: (state) => {
      state.isBusy = false;
      state.dayComment = undefined;
      state.dayScore = undefined;
    },
    setSelectedView: (state, action: PayloadAction<CalendarView>) => {
      state.selectedView = action.payload;
    },
  },
});

export const {
  setDayComment,
  setDayScore,
  setCalendarEntries,
  addCalendarEntryRequest,
  addCalendarEntryResponse,
  setDisplayedDate,
  setDisplayedYear,
  deleteCalendarEntryRequest,
  deleteCalendarEntryResponse,
  setSelectedView
} = calendarSlice.actions;

export const calendarReducer = calendarSlice.reducer;
